export interface NEWS2Parameters {
  respirationRate?: number;
  oxygenSaturation?: number;
  systolicBloodPressure?: number;
  pulseRate?: number;
  consciousness?: "Alert" | "Voice" | "Pain" | "Unresponsive" | "Confusion";
  temperature?: number;
  supplementalOxygen?: boolean;
}

export interface NEWS2Score {
  total: number;
  respirationRate: number;
  oxygenSaturation: number;
  systolicBloodPressure: number;
  pulseRate: number;
  consciousness: number;
  temperature: number;
  supplementalOxygen: number;
  riskLevel: "none" | "low" | "low-medium" | "medium" | "high";
  clinicalResponse: string;
}

export const extractSystolicBP = (value: string): number | undefined => {
  if (!value) return undefined;
  const parts = value.split("/");
  const sys = parseFloat(parts[0]);
  return isNaN(sys) ? undefined : sys;
};

const scoreRespirationRate = (rr: number): number => {
  if (rr <= 8) return 3;
  if (rr <= 11) return 1;
  if (rr <= 20) return 0;
  if (rr <= 24) return 2;
  return 3;
};

const scoreOxygenSaturation = (spo2: number): number => {
  if (spo2 <= 91) return 3;
  if (spo2 <= 93) return 2;
  if (spo2 <= 95) return 1;
  return 0;
};

const scoreSpO2Scale1 = (spo2: number): number => {
  if (spo2 <= 91) return 3;
  if (spo2 <= 93) return 2;
  if (spo2 <= 95) return 1;
  return 0; // ≥96
};

const scoreSpO2Scale2 = (spo2: number): number => {
  // Used when patient is on supplemental oxygen
  if (spo2 <= 83) return 3;
  if (spo2 <= 85) return 2;
  if (spo2 <= 87) return 1;
  if (spo2 <= 92) return 0; // 88-92 on O2
  if (spo2 <= 94) return 1; // 93-94 on O2
  if (spo2 <= 96) return 2; // 95-96 on O2
  return 3;                  // ≥97 on O2
};

const scoreSystolicBP = (sbp: number): number => {
  if (sbp <= 90) return 3;
  if (sbp <= 100) return 2;
  if (sbp <= 110) return 1;
  if (sbp <= 219) return 0;
  return 3;
};

const scorePulseRate = (hr: number): number => {
  if (hr <= 40) return 3;
  if (hr <= 50) return 1;
  if (hr <= 90) return 0;
  if (hr <= 110) return 1;
  if (hr <= 130) return 2;
  return 3;
};

const scoreConsciousness = (
  level: NEWS2Parameters["consciousness"],
): number => {
  if (level === "Alert") return 0;
  return 3; // Voice, Pain, Unresponsive, Confusion all = 3
};

const scoreTemperature = (temp: number): number => {
  if (temp <= 35.0) return 3;
  if (temp <= 36.0) return 1;
  if (temp <= 38.0) return 0;
  if (temp <= 39.0) return 1;
  return 2;
};

export const calculateNEWS2 = (params: NEWS2Parameters): NEWS2Score => {
  const scores = {
    respirationRate:
      params.respirationRate !== undefined
        ? scoreRespirationRate(params.respirationRate)
        : 0,
   oxygenSaturation: params.oxygenSaturation !== undefined
  ? params.supplementalOxygen === true
    ? scoreSpO2Scale2(params.oxygenSaturation)
    : scoreSpO2Scale1(params.oxygenSaturation)
  : 0,
    systolicBloodPressure:
      params.systolicBloodPressure !== undefined
        ? scoreSystolicBP(params.systolicBloodPressure)
        : 0,
    pulseRate:
      params.pulseRate !== undefined ? scorePulseRate(params.pulseRate) : 0,
    consciousness:
      params.consciousness !== undefined
        ? scoreConsciousness(params.consciousness)
        : 0,
    temperature:
      params.temperature !== undefined
        ? scoreTemperature(params.temperature)
        : 0,
    supplementalOxygen: params.supplementalOxygen === true ? 2 : 0,
  };

  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const hasAnySingleParamThree = Object.values(scores).some((s) => s >= 3);

  let riskLevel: NEWS2Score["riskLevel"] = "none";
  let clinicalResponse = "Minimum: Routine monitoring every 12 hours.";

  if (total >= 7) {
    riskLevel = "high";
    clinicalResponse =
      "High: Emergency assessment by clinical team. Continuous monitoring.";
  } else if (total >= 5 || (total >= 1 && hasAnySingleParamThree)) {
    riskLevel = total >= 5 ? "medium" : "low-medium";
    clinicalResponse =
      "Low-Medium: Urgent review by ward-based doctor. Monitoring every 1 hour.";
  } else if (total >= 1) {
    riskLevel = "low";
    clinicalResponse =
      "Low: Increase frequency of monitoring to every 4–6 hours.";
  }

  return { total, ...scores, riskLevel, clinicalResponse };
};